import { useEffect, useReducer, useState } from 'react';
import axios from 'axios';

import { useRealTimeUpdates } from '~/components/AiChat/hooks/useRealTimeUpdates';
import type { IssueTokenResponse } from '~/components/AiChat/types';
import useDataFetcher from '~/components/useDataFetcher';
import type { ClaimConductorStatusResponse } from '~/server_shared/openapi/data-contracts';
import { conductorStatusFirestoreService } from '~/services/firestore/firestoreService';

import { type ConductorStatusObservation, toPublishedObservation } from '../conductorStatusObservation';

/** Each read of the status route reaches claims_llm, and the topic carries every change between two reads. */
export const STATUS_POLL_INTERVAL_MS = 5 * 60 * 1000;

interface ConductorStatusTokenApiResponse {
  issue_token_response: IssueTokenResponse | null;
}

interface StatusState {
  observation: ConductorStatusObservation | null;
  /** Whether the status route has answered since the claim, or who drives it, last changed. */
  hasPolled: boolean;
  /** Whether `observation` is the topic's first snapshot rather than a live publish or the route's answer. */
  isFromSnapshot: boolean;
}

type StatusAction =
  | { type: 'polled'; observation: ConductorStatusObservation }
  | { type: 'published'; observation: ConductorStatusObservation; isHistorical: boolean }
  | { type: 'reset' };

/** The real-time token that opens the claim's `conductor_status` topic. */
const fetchConductorStatusToken = async (claimId: number): Promise<IssueTokenResponse | null> => {
  const { data } = await axios.get<ConductorStatusTokenApiResponse>(
    `/api/v1/claims/${claimId}/conductor-hub/status-token`
  );
  return data.issue_token_response;
};

const INITIAL_STATE: StatusState = { observation: null, hasPolled: false, isFromSnapshot: false };

/**
 * The latest observation wins: the status route reads the record each publish follows, so it is never behind
 * the topic. Except the topic's first snapshot, which holds what was published before the subscription opened:
 * once the route has answered, that would put back a status the record has since left without publishing -
 * Working, for a run whose worker died - until the next poll. A status published between the route's read and
 * that snapshot is then missed until the next publish or poll.
 */
const reduceStatus = (state: StatusState, action: StatusAction): StatusState => {
  switch (action.type) {
    case 'polled':
      return { observation: action.observation, hasPolled: true, isFromSnapshot: false };
    case 'published':
      if (action.isHistorical && state.hasPolled) return state;
      return { ...state, observation: action.observation, isFromSnapshot: action.isHistorical };
    case 'reset':
      return INITIAL_STATE;
  }
};

/**
 * Clive's status on the claim while `enabled`, or null while nothing is known of it - or while all that is known
 * is the topic's first snapshot, until the status route has answered or failed.
 *
 * Read live from the claim's `conductor_status` topic, and from the status route on mount and every five minutes:
 * the backstop for a worker that died without publishing its ending, and for a publish that was lost. A failed
 * read of either keeps the status there is, and the server has logged it, so neither is reported here.
 */
export const useConductorStatus = (
  claimId: number,
  { enabled }: { enabled: boolean }
): ConductorStatusObservation | null => {
  const [state, dispatch] = useReducer(reduceStatus, INITIAL_STATE);
  const [issueTokenResponse, setIssueTokenResponse] = useState<IssueTokenResponse | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let isCancelled = false;
    fetchConductorStatusToken(claimId)
      .then((token) => {
        if (!isCancelled) setIssueTokenResponse(token);
      })
      .catch(() => {
        // The status route alone keeps the status current.
      });

    return () => {
      isCancelled = true;
      // So the subscription ends with the claim, or the handling, it was opened for.
      setIssueTokenResponse(null);
    };
  }, [claimId, enabled]);

  const { events, hasReceivedInitialSnapshot } = useRealTimeUpdates({
    issueTokenResponse,
    enabled: enabled && Boolean(issueTokenResponse),
    service: conductorStatusFirestoreService,
    // The token's query reads the newest document first, so a window of one holds the current status.
    eventLimit: 1,
  });
  const [latestEvent] = events;

  const { data: polledStatus, isError: hasPollFailed } = useDataFetcher<ClaimConductorStatusResponse>(
    `/api/v1/claims/${claimId}/conductor/status`,
    null,
    { shouldFetch: enabled, shouldReportAxiosError: false, refetchInterval: STATUS_POLL_INTERVAL_MS }
  );

  useEffect(() => {
    if (!polledStatus) return;
    dispatch({
      type: 'polled',
      observation: {
        conductorStatus: polledStatus.conductor_status,
        isActive: polledStatus.is_active,
        openAsks: polledStatus.open_asks,
        runId: polledStatus.run_id,
      },
    });
  }, [polledStatus]);

  useEffect(() => {
    // An empty topic does not say Clive never ran - its last run may predate the publisher - so only the
    // status route says that.
    if (!hasReceivedInitialSnapshot || !latestEvent) return;
    // A document without a readable status is ignored: the next one, or the next poll, replaces the status there is.
    const observation = toPublishedObservation(latestEvent.metadata);
    if (observation) {
      dispatch({ type: 'published', observation, isHistorical: latestEvent._isHistorical === true });
    }
  }, [hasReceivedInitialSnapshot, latestEvent]);

  useEffect(() => () => dispatch({ type: 'reset' }), [claimId, enabled]);

  // The topic's first snapshot can be a status the record has since left, so it waits on the route's answer -
  // shown only if the route fails - rather than flashing before the route replaces it. A live publish is newer
  // than the route's read, so it is shown at once.
  return state.isFromSnapshot && !state.hasPolled && !hasPollFailed ? null : state.observation;
};
